/**
 * Validación y diff de la configuración de la cuenta. Puro.
 *
 * Los rangos son IDÉNTICOS a los del backend (SPEC-backend §9.1, §9.2 y §6.12). Validar acá sólo
 * sirve para marcar el error bajo el campo antes de mandar; si igual algo pasa, el backend lo
 * rechaza con 422 BAD_INPUT y la pantalla muestra ese mensaje.
 */

import type { ConfigCuenta, CredencialesEntrada, CuentaPublica, FranjaHoraria, LimiteMetaElegible, LimiteMetaPublico, LinkShorteningConfig } from "../types";
import type { RespuestaRapida } from "../types.conversaciones";
import { num } from "../../campana-nueva/utils/format";
import { normalizarTexto } from "./base";

/**
 * Claves que nunca se mandan en el PATCH de config: `franjasTexto` la calcula el backend, y
 * `alertasTwilio` se cambia desde Conexión (técnico) por su endpoint (el PATCH la rechaza con 422:
 * si se colara en un diff, un guardado de Envío fallaría entero por algo que el operador no tocó).
 */
const SOLO_LECTURA = new Set<keyof ConfigCuenta>(["franjasTexto", "alertasTwilio"]);

// ── Campos nuevos con default (SPEC-estadisticas-conexion §A.2.1) ────────────────────────────
// En el tipo son opcionales (un backend viejo o un test no los trae). Link Shortening se lee SIEMPRE
// con este helper: el default es el mismo del backend y es el lado seguro (links sin acortar).
// `alertasTwilio` y `complianceConfirmado` no tienen helper acá porque ninguna pantalla los lee de
// la config: las alertas llegan por su endpoint (TarjetaAlertasGasto) y la confirmación de
// compliance, por el diagnóstico (PanelDiagnostico).

export const LINK_SHORTENING_POR_DEFECTO: Readonly<LinkShorteningConfig> = Object.freeze({ activo: false, enSesiones: true, enPlantillas: false });

/**
 * Link Shortening con default. `activo` sólo es true si vino `true` literal: cualquier otra cosa
 * lo deja apagado (cuesta USD 0,015 por mensaje: prenderlo por un dato raro es gastar sin querer).
 */
export function linkShorteningDe(c: Partial<ConfigCuenta> | null | undefined): LinkShorteningConfig {
  const l = c?.linkShortening;
  if (!l || typeof l !== "object") return { ...LINK_SHORTENING_POR_DEFECTO };
  return {
    activo: l.activo === true,
    enSesiones: typeof l.enSesiones === "boolean" ? l.enSesiones : LINK_SHORTENING_POR_DEFECTO.enSesiones,
    enPlantillas: l.enPlantillas === true,
  };
}

/** JSON con claves ordenadas: compara franjas y respuestaAutomatica en profundidad. */
function canon(x: unknown): string {
  const orden = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(orden);
    if (v && typeof v === "object") {
      const o = v as Record<string, unknown>;
      return Object.keys(o).sort().reduce<Record<string, unknown>>((acc, k) => {
        if (o[k] !== undefined) acc[k] = orden(o[k]);
        return acc;
      }, {});
    }
    return v;
  };
  return JSON.stringify(orden(x) ?? null);
}

/** Sólo las claves que cambiaron (comparación profunda de franjas y respuestaAutomatica). */
export function diffConfig(original: ConfigCuenta, editado: ConfigCuenta): Partial<ConfigCuenta> {
  const out: Partial<ConfigCuenta> = {};
  for (const k of Object.keys(editado) as (keyof ConfigCuenta)[]) {
    if (SOLO_LECTURA.has(k)) continue;
    if (canon(original?.[k]) !== canon(editado[k])) (out as Record<string, unknown>)[k] = editado[k];
  }
  return out;
}

const esEntero = (n: unknown) => typeof n === "number" && Number.isInteger(n);
const esNumero = (n: unknown) => typeof n === "number" && Number.isFinite(n);
const HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

export const MSG_FRANJA_VACIA = "Tiene que haber al menos una franja: sin franja se enviaría a cualquier hora.";
export const MSG_FRANJA_IGUAL = "La hora de inicio y la de fin no pueden ser iguales.";

// ── Conversaciones: mail de avisos y respuestas rápidas (spec de conversaciones §2.6 y §11) ─────────
// La regla es la de `validarParcialConfig` del backend: trim antes de medir, "" vale (sin aviso), títulos
// sin repetir sin mayúsculas ni acentos («Horario» y «horário» son el mismo botón en la caja).

export const MAX_RESPUESTAS_RAPIDAS = 5;
export const MAX_TITULO_RAPIDA = 40;
export const MAX_TEXTO_RAPIDA = 1024;
const MAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_MAIL = 200;
export const MSG_MAIL_AVISOS = "Escribí un mail válido o dejalo vacío.";
export const MSG_RESPUESTAS_RAPIDAS = "Cada respuesta rápida necesita título (hasta 40) y texto (hasta 1.024), sin títulos repetidos.";
export const MSG_TOPE_RESPUESTAS_RAPIDAS = "Podés guardar hasta 5 respuestas rápidas.";

function errorRespuestasRapidas(v: unknown): string {
  if (!Array.isArray(v)) return MSG_RESPUESTAS_RAPIDAS;
  if (v.length > MAX_RESPUESTAS_RAPIDAS) return MSG_TOPE_RESPUESTAS_RAPIDAS;
  const titulos = new Set<string>();
  for (const x of v as Partial<RespuestaRapida>[]) {
    const titulo = typeof x?.titulo === "string" ? x.titulo.trim() : "";
    const texto = typeof x?.texto === "string" ? x.texto.trim() : "";
    if (!titulo || titulo.length > MAX_TITULO_RAPIDA || !texto || texto.length > MAX_TEXTO_RAPIDA) return MSG_RESPUESTAS_RAPIDAS;
    const clave = normalizarTexto(titulo);
    if (titulos.has(clave)) return MSG_RESPUESTAS_RAPIDAS;
    titulos.add(clave);
  }
  return "";
}

/**
 * El id de una respuesta rápida nueva: 8 hex, como el que el backend acepta sin regenerar. Se genera al
 * AGREGAR la fila: con `id: ""` el backend ponía uno propio, lo guardado nunca coincidía con lo editado y
 * «Guardar cambios» quedaba habilitado para siempre (y cada guardado cambiaba los ids).
 */
export function idRespuestaRapida(existentes: string[]): string {
  const usados = new Set(existentes);
  for (;;) {
    const b = new Uint8Array(4);
    globalThis.crypto.getRandomValues(b);
    const id = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    if (!usados.has(id)) return id;
  }
}

/** Trim de título y texto antes de guardar: el backend lo hace igual, y un espacio de más dejaba el formulario con cambios. */
export function limpiarRespuestasRapidas(r: RespuestaRapida[] | undefined): RespuestaRapida[] {
  return (Array.isArray(r) ? r : []).map((x) => ({ id: x.id, titulo: String(x.titulo ?? "").trim(), texto: String(x.texto ?? "").trim() }));
}

/** Errores por campo con los mismos rangos que el backend. Vacío = ok. */
export function validarConfigLocal(c: Partial<ConfigCuenta>): Record<string, string> {
  const e: Record<string, string> = {};
  const tiene = (k: keyof ConfigCuenta) => Object.prototype.hasOwnProperty.call(c, k);

  if (tiene("ritmoMps") && !(esEntero(c.ritmoMps) && c.ritmoMps! >= 1 && c.ritmoMps! <= 80)) e.ritmoMps = "Entre 1 y 80 mensajes por segundo.";
  if (tiene("cupoManual24h") && c.cupoManual24h !== null && !(esEntero(c.cupoManual24h) && c.cupoManual24h! >= 1)) {
    e.cupoManual24h = "Dejalo vacío para usar el límite de Meta, o poné un número mayor que 0.";
  }
  if (tiene("fatigaDias") && !(esEntero(c.fatigaDias) && c.fatigaDias! >= 0 && c.fatigaDias! <= 30)) e.fatigaDias = "Entre 0 (sin límite) y 30 días.";
  // la regla de la marca (29/10/2026): como mucho K mensajes cada `fatigaDias` días, sumando WhatsApp oficial y por QR
  if (tiene("fatigaMaximo") && c.fatigaMaximo !== undefined && !(esEntero(c.fatigaMaximo) && c.fatigaMaximo! >= 1 && c.fatigaMaximo! <= 10)) {
    e.fatigaMaximo = "Entre 1 y 10 mensajes.";
  }
  if (tiene("fatigaContarMails") && c.fatigaContarMails !== undefined && typeof c.fatigaContarMails !== "boolean") e.fatigaContarMails = "Tiene que ser sí o no.";
  if (tiene("diasCaliente") && c.diasCaliente !== undefined && !(esEntero(c.diasCaliente) && c.diasCaliente! >= 0 && c.diasCaliente! <= 365)) {
    e.diasCaliente = "Entre 0 (sólo los que escribieron) y 365 días.";
  }
  if (tiene("recargoPct") && !(esNumero(c.recargoPct) && c.recargoPct! >= 0 && c.recargoPct! <= 100)) e.recargoPct = "Entre 0 % y 100 %.";
  if (tiene("topeMensualUSD") && c.topeMensualUSD !== null && !(esNumero(c.topeMensualUSD) && c.topeMensualUSD! > 0)) {
    e.topeMensualUSD = "Dejalo vacío para no tener tope, o poné un monto mayor que 0.";
  }
  if (tiene("umbralSaldoBajoUSD") && !(esNumero(c.umbralSaldoBajoUSD) && c.umbralSaldoBajoUSD! >= 0)) e.umbralSaldoBajoUSD = "Tiene que ser 0 o más.";
  if (tiene("textoBaja")) {
    const t = String(c.textoBaja ?? "");
    // «BAJA» en mayúsculas: es la palabra clave de la baja automática de Twilio (Advanced Opt-Out).
    if (t.trim().length < 1 || t.length > 60 || !t.includes("BAJA")) {
      e.textoBaja = "Hasta 60 caracteres y tiene que decir BAJA en mayúsculas: es la palabra que da de baja.";
    }
  }
  if (tiene("botonBaja")) {
    const t = String(c.botonBaja ?? "");
    if (t.trim().length < 1 || t.length > 20) e.botonBaja = "Hasta 20 caracteres.";
  }
  if (tiene("confirmacionBaja")) {
    const t = String(c.confirmacionBaja ?? "");
    if (t.trim().length < 1 || t.length > 1024) e.confirmacionBaja = "Entre 1 y 1024 caracteres.";
  }
  if (tiene("respuestaAutomatica")) {
    const r = c.respuestaAutomatica;
    const texto = String(r?.texto ?? "");
    if (!r || texto.length > 1024 || (r.activa && !texto.trim())) e.respuestaAutomatica = "Escribí la respuesta o apagala.";
  }
  if (tiene("franjas")) {
    const errs = validarFranjasLocal(c.franjas || []);
    if (errs.length) e.franjas = errs.join(" ");
  }
  if (tiene("idioma") && !["es", "es_AR"].includes(String(c.idioma))) e.idioma = "Elegí Español o Español de Argentina.";
  if (tiene("mailAvisos")) {
    const m = typeof c.mailAvisos === "string" ? c.mailAvisos.trim() : null;
    if (m === null || (m !== "" && (m.length > MAX_MAIL || !MAIL.test(m)))) e.mailAvisos = MSG_MAIL_AVISOS;
  }
  if (tiene("respuestasRapidas")) {
    const err = errorRespuestasRapidas(c.respuestasRapidas);
    if (err) e.respuestasRapidas = err;
  }
  return e;
}

const REGLA_CREDENCIAL: Record<keyof CredencialesEntrada, { re: RegExp; msg: string }> = {
  accountSid: { re: /^AC[0-9a-f]{32}$/, msg: "Empieza con AC y tiene 34 caracteres." },
  authToken: { re: /^[0-9a-f]{32}$/, msg: "32 caracteres hexadecimales." },
  messagingServiceSid: { re: /^MG[0-9a-f]{32}$/, msg: "Empieza con MG y tiene 34 caracteres." },
  senderE164: { re: /^\+\d{8,15}$/, msg: "En formato internacional: +5491155443322." },
};

/**
 * Credenciales de Twilio: mismo formato que exige el backend. undefined = el campo está bien.
 *
 * Valida SÓLO las claves presentes, porque `PUT /cuenta/credenciales` tiene tres formas (§A.4.5):
 * la completa (los cuatro campos), la corta `{ accountSid, authToken }` (Sandbox y `conectar`) y
 * la rotación `{ authToken }`. Pedir un MG en una rotación bloquearía cambiar el token.
 */
export function validarCredencialesLocal(c: CredencialesEntrada): Record<keyof CredencialesEntrada, string | undefined>;
export function validarCredencialesLocal<K extends keyof CredencialesEntrada>(c: Pick<CredencialesEntrada, K>): Record<K, string | undefined>;
export function validarCredencialesLocal(c: Partial<CredencialesEntrada>): Partial<Record<keyof CredencialesEntrada, string | undefined>> {
  const out: Partial<Record<keyof CredencialesEntrada, string | undefined>> = {};
  const o = (c && typeof c === "object" ? c : {}) as Partial<CredencialesEntrada>;
  for (const k of Object.keys(REGLA_CREDENCIAL) as (keyof CredencialesEntrada)[]) {
    if (!Object.prototype.hasOwnProperty.call(o, k)) continue;
    const v = String(o[k] ?? "").trim();
    out[k] = REGLA_CREDENCIAL[k].re.test(v) ? undefined : REGLA_CREDENCIAL[k].msg;
  }
  return out;
}

/**
 * Franjas horarias: de 1 a 7; `HH:MM`; `desde ≠ hasta` (`desde > hasta` cruza la medianoche y es
 * válido); días 0–6 sin repetir (vacío o ausente = todos los días). Misma regla que
 * `validarFranjas` del backend. Una lista vacía NO es «sin restricción»: en el oficial es cerrado,
 * y se rechaza para que nadie crea que dejó el envío abierto.
 */
export function validarFranjasLocal(f: FranjaHoraria[]): string[] {
  if (!Array.isArray(f) || f.length === 0) return [MSG_FRANJA_VACIA];
  const errs: string[] = [];
  if (f.length > 7) errs.push("Hasta 7 franjas.");
  // Con varias franjas se dice cuál; con una sola, el mensaje va solo (y con mayúscula).
  const msg = (i: number, t: string) => (f.length > 1 ? `Franja ${i + 1}: ${t.charAt(0).toLowerCase()}${t.slice(1)}` : `${t.charAt(0).toUpperCase()}${t.slice(1)}`);
  f.forEach((x, i) => {
    const desde = String(x?.desde || "");
    const hasta = String(x?.hasta || "");
    if (!HORA.test(desde) || !HORA.test(hasta)) {
      errs.push(msg(i, "La hora va en formato HH:MM (de 00:00 a 23:59)."));
      return;
    }
    if (desde === hasta) errs.push(msg(i, MSG_FRANJA_IGUAL));
    if (x.dias !== undefined) {
      const d = x.dias;
      const validos = Array.isArray(d) && d.every((n) => Number.isInteger(n) && n >= 0 && n <= 6) && new Set(d).size === d.length;
      if (!validos) errs.push(msg(i, "Los días van de domingo (0) a sábado (6), sin repetir."));
    }
  });
  return errs;
}

/** Recarga de saldo: 0 < monto ≤ 10.000 USD y comprobante de 1 a 200 caracteres. */
export function validarRecarga(r: { montoUSD: number; comprobante: string }): Record<string, string> {
  const e: Record<string, string> = {};
  if (!(esNumero(r?.montoUSD) && r.montoUSD > 0 && r.montoUSD <= 10000)) e.montoUSD = "Un monto mayor que 0 y de hasta USD 10.000.";
  const c = String(r?.comprobante || "").trim();
  if (!c || c.length > 200) e.comprobante = "Escribí el comprobante (hasta 200 caracteres).";
  return e;
}

/** Ajuste de saldo: monto distinto de 0, |monto| ≤ 10.000 y motivo obligatorio (queda en la auditoría). */
export function validarAjuste(a: { montoUSD: number; motivo: string }): Record<string, string> {
  const e: Record<string, string> = {};
  if (!(esNumero(a?.montoUSD) && a.montoUSD !== 0 && Math.abs(a.montoUSD) <= 10000)) e.montoUSD = "Un monto distinto de 0 y de hasta USD 10.000 (negativo para restar).";
  if (!String(a?.motivo || "").trim()) e.motivo = "Contá por qué ajustás el saldo.";
  return e;
}

// ── El límite de Meta (Envío → «Límite diario») ───────────────────────────────────────────────

/**
 * Lo que va entre paréntesis en «Usar el límite de Meta (…)». Con el que cargó el equipo (Twilio dice «Unavailable»)
 * lo aclara: si no, «2.000» se leería como un dato de Twilio. Sin `limiteMeta` (un backend anterior), con el sender.
 */
export function textoLimiteMeta(cuenta: Pick<CuentaPublica, "sender" | "limiteMeta">, sinNumero: boolean): string {
  if (sinNumero) return "sin número todavía: Meta arranca con 250";
  const l = cuenta.limiteMeta;
  if (l?.fuente === "equipo") return `${l.ilimitado ? "sin límite" : `${num(Number(l.limite))} personas cada 24 h`}, el que cargó el equipo`;
  const s = cuenta.sender;
  if (s?.messagingLimitIlimitado) return "sin límite";
  return s?.messagingLimit != null ? `${num(s.messagingLimit)} personas cada 24 h` : "sin dato: se usan 250 personas cada 24 h";
}

/** Las opciones del límite de WhatsApp Manager. Sin el 250: es el mínimo, el que ya se usa si no se carga nada. */
export const OPCIONES_LIMITE_META: readonly { valor: LimiteMetaElegible | null; etiqueta: string }[] = [
  { valor: null, etiqueta: "No cargar nada (se usa lo que diga Twilio)" },
  { valor: 2000, etiqueta: "2.000 personas cada 24 h" },
  { valor: 10000, etiqueta: "10.000 personas cada 24 h" },
  { valor: 100000, etiqueta: "100.000 personas cada 24 h" },
  { valor: "ilimitado", etiqueta: "Sin límite" },
];

/** Lo cargado como una opción del selector (null si no hay nada o no es uno de los niveles que se ofrecen). */
export function limiteElegidoDe(confirmado: LimiteMetaPublico["confirmado"] | undefined): LimiteMetaElegible | null {
  if (!confirmado) return null;
  if (confirmado.ilimitado) return "ilimitado";
  return OPCIONES_LIMITE_META.find((o) => o.valor === confirmado.limite)?.valor ?? null;
}
